import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Trans } from '@lingui/react';
import { i18n } from 'i18n';
import { FormikHelpers } from 'formik';
import { ProjectNumberField } from 'components/Form';
import useForm from 'hooks/useForm';
import {
  additionalUnitLabels,
  BasicDisplayUnits,
  getPressurePumpUnitLabel,
  unitLabels
} from 'hooks/useDisplayUnits/useDisplayUnits';
import ProjectContext from 'modules/project/providers/project/context';
import {
  BackfillRecipeDto,
  DisplayUnits,
  FlushRecipeDto,
  FlushRecipeForm,
  FlushVolumeMode,
  MetricPressureUnit,
  RheologyDataDto,
  UnitSystem,
  useCreateFlushRecipeMutation,
  useUpdateFlushRecipeMutation
} from 'providers/api';
import React from 'react';
import { round } from 'utils';
import { sanitiseNumber } from 'utils/form';
import { pumpPressureBarToKpa, pumpPressureKpaToBar } from 'utils/unitConversions';
import { toFlushResultDto, toFlushSpec } from '../../../MineModel/Flushing/flushRecipeMapping';
import { solveFlush } from '../../../MineModel/Flushing/flushSolver';
import { FlushResult } from '../../../MineModel/Flushing/flushTypes';
import { FLUSH_VOLUME_MODES, flushVolumeModeLabel, flushVolumeValueLabel } from '../../../MineModel/Flushing/flushLabels';
import { Model } from '../../../hooks/useModelState';
import { findStoredRoute, pickFlushRoute } from '../../recipeModelUtils';
import FlushDetails from '../FlushDetails';
import { FlushScrubContext } from '../FlushScrub';
import FormActions from '../RecipeForm/FormActions';
import UnitSystemNumberField from '../RecipeForm/UnitSystemNumberField';
import generateFlushSchema from './FlushSchema';
import {
  FlushFormValues,
  flushFormInitialValues,
  pasteFromRecipe,
  RecipePaste
} from './flushFormHelpers';

const PREFIX = 'FlushForm';

const classes = {
  fullWidthDivider: `${PREFIX}-fullWidthDivider`,
  form: `${PREFIX}-form`,
  section: `${PREFIX}-section`,
  checkboxCell: `${PREFIX}-checkboxCell`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.fullWidthDivider}`]: {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.form}`]: {
    width: '100%',
  },
  [`& .${classes.section}`]: {
    marginTop: theme.spacing(1),
  },
  [`& .${classes.checkboxCell}`]: {
    display: 'flex',
    alignItems: 'flex-end',
  },
}));

const PRESSURE_DECIMAL_PLACES_BAR = 1;
const FLOW_RATE_DECIMAL_PLACES = 1;
const pastePumpHelpText = i18n._('The paste pump pushes the first water at the recipe flow rate until the flush pump can take over');

// Owns the HGL overlay while a flush that has no id yet is being previewed.
const NEW_FLUSH_SCRUB_KEY = 'new-flush';

interface FlushFormProps {
  model: Model;
  flushRecipe?: FlushRecipeDto;
  recipes: BackfillRecipeDto[];
  rheologyById: Record<string, RheologyDataDto>;
  onSuccess: (flushRecipeId: string) => void;
  onCancel: () => void;
}

const FlushForm = ({ model, flushRecipe, recipes, rheologyById, onSuccess, onCancel }: FlushFormProps) => {
  const projectContext = React.useContext(ProjectContext);
  const { project, selectedStope } = model;
  const { entityId: projectId, unitSystemPreference, customUnitFields } = project;
  const stopeId = selectedStope?.data.stopeId ?? '';

  const metricPressureUnit = projectContext?.state.metricPressureUnit ?? MetricPressureUnit.Bar;
  const isKiloPascal = metricPressureUnit === MetricPressureUnit.KiloPascal && unitSystemPreference === UnitSystem.Metric;
  const pressureUnitInfo = getPressurePumpUnitLabel(metricPressureUnit);
  const pressureDecimalPlaces = isKiloPascal ? 0 : PRESSURE_DECIMAL_PLACES_BAR;
  const toDisplayPressure = (bar: number) => (isKiloPascal ? pumpPressureBarToKpa(bar) : bar);
  const fromDisplayPressure = (display: number) => (isKiloPascal ? pumpPressureKpaToBar(display) : display);
  const flowRateUnitInfo = additionalUnitLabels[DisplayUnits.FlowRate];

  // Only recipes whose rheology still exists can describe the paste ahead of the water.
  const linkableRecipes = recipes.filter((recipe) => !!rheologyById[recipe.rheologyDataSetId]);
  const defaultRecipe = (flushRecipe?.backfillRecipeId
    ? linkableRecipes.find((recipe) => recipe.entityId === flushRecipe.backfillRecipeId)
    : undefined) ?? linkableRecipes[0];

  const storedSpoolKey = flushRecipe?.spoolIds.join(',');
  const storedRouteSpoolKey = flushRecipe?.routeSpoolIds?.join(',');
  // Keyed on primitives: the model's objects change identity on every render, and every network save bumps loadGeneration.
  const storedRoute = React.useMemo(
    () => (flushRecipe ? findStoredRoute(model, flushRecipe.spoolIds, flushRecipe.routeSpoolIds) : undefined),
    [flushRecipe?.entityId, storedSpoolKey, storedRouteSpoolKey, model.loadGeneration, stopeId],
  );
  const { route, replanning } = pickFlushRoute(storedRoute, model.getSelectedRoute(), model.routes, !!flushRecipe);

  const resolvePaste = (recipeId: string): RecipePaste | undefined => {
    const recipe = linkableRecipes.find((candidate) => candidate.entityId === recipeId);
    const rheology = recipe && rheologyById[recipe.rheologyDataSetId];
    return recipe && rheology ? pasteFromRecipe(model, recipe, rheology) : undefined;
  };

  const solveFor = (values: FlushFormValues): FlushResult | undefined => {
    const { specification } = values;
    const recipePaste = resolvePaste(values.backfillRecipeId);
    const canSolve = !!route
      && !!recipePaste
      && specification.flushPumpMaxPressure > 0
      && specification.flushPumpFlowRate > 0
      && specification.pastePumpFlowRate > 0
      && (!specification.airFlushEnabled || (specification.airFlushMaxPressure ?? 0) > 0);
    if (!canSolve || !route || !recipePaste) return undefined;
    try {
      return solveFlush({ spools: route.spools, routePointStrings: route.routePointStrings }, toFlushSpec(specification), recipePaste.paste);
    } catch {
      return undefined;
    }
  };

  const createMutation = useCreateFlushRecipeMutation();
  const updateMutation = useUpdateFlushRecipeMutation(flushRecipe?.entityId ?? '');
  const mutation = flushRecipe ? updateMutation : createMutation;

  const { formik, helpers } = useForm<FlushFormValues>({
    mutation,
    formikConfig: {
      initialValues: flushFormInitialValues(project, flushRecipe, defaultRecipe?.entityId, resolvePaste(defaultRecipe?.entityId ?? '')?.wetFlowRate),
      validationSchema: generateFlushSchema(project.flushPumpMaxFlowRate ?? undefined),
      validateOnMount: true,
      onSubmit: async (values: FlushFormValues, { setSubmitting }: FormikHelpers<FlushFormValues>) => {
        const linkedRecipe = linkableRecipes.find((recipe) => recipe.entityId === values.backfillRecipeId);
        const solved = solveFor(values);
        if (!route || !linkedRecipe || !solved) return;

        const data: FlushRecipeForm = {
          reference: values.reference,
          rheologyDataSetId: linkedRecipe.rheologyDataSetId,
          backfillRecipeId: linkedRecipe.entityId,
          spools: route.routePointStrings,
          routeSpools: route.spoolIds,
          stopeId,
          specification: {
            ...values.specification,
            airFlushMaxPressure: values.specification.airFlushEnabled ? values.specification.airFlushMaxPressure : undefined,
          },
          result: toFlushResultDto(solved),
        };

        setSubmitting(true);
        try {
          if (flushRecipe) {
            await updateMutation.mutateAsync({ projectId, stopeId, flushRecipeId: flushRecipe.entityId, data });
            onSuccess(flushRecipe.entityId);
          } else {
            const created = await createMutation.mutateAsync({ projectId, stopeId, data });
            onSuccess(created);
          }
        } finally {
          setSubmitting(false);
        }
      },
    },
  });

  const {
    values,
    isValid,
    isSubmitting,
    dirty,
    resetForm,
    handleSubmit,
    handleBlur,
    setFieldValue,
  } = formik;
  const { hasError, getErrorHelpText } = helpers;
  const { specification } = values;

  const linkedRecipe = linkableRecipes.find((recipe) => recipe.entityId === values.backfillRecipeId);
  // The solve depends on the route object and the form values; the model's other fields do not change the answer.
  const result = React.useMemo(
    () => solveFor(values),
    [values, route, linkableRecipes, rheologyById, project.fillType],
  );
  const scrubPaste = React.useMemo(
    () => resolvePaste(values.backfillRecipeId)?.paste,
    [values.backfillRecipeId, linkableRecipes, rheologyById, project.fillType],
  );
  const scrub = React.useMemo<FlushScrubContext | undefined>(
    () => (route && scrubPaste ? { scrubKey: flushRecipe?.entityId ?? NEW_FLUSH_SCRUB_KEY, route, paste: scrubPaste } : undefined),
    [route, scrubPaste, flushRecipe?.entityId],
  );

  const handleRecipeChange = (recipeId: string) => {
    setFieldValue('backfillRecipeId', recipeId);
    const wetFlowRate = resolvePaste(recipeId)?.wetFlowRate;
    if (wetFlowRate) {
      setFieldValue('specification.pastePumpFlowRate', round(wetFlowRate, FLOW_RATE_DECIMAL_PLACES));
    }
  };

  const volumeValueField = (() => {
    switch (specification.volumeMode) {
      case FlushVolumeMode.AirStartVolume:
        return null;
      case FlushVolumeMode.PipeVolumeMultiple:
        return (
          <ProjectNumberField
            fullWidth
            color="secondary"
            variant="outlined"
            size="small"
            id="flush-volume-value"
            name="specification.volumeValue"
            decimalPlaces={2}
            value={specification.volumeValue}
            aria-labelledby="flush-volume-value-helper-text"
            onChange={(parsed) => setFieldValue('specification.volumeValue', parsed ?? 0)}
            onBlur={handleBlur}
            error={hasError('specification.volumeValue')}
            helperText={getErrorHelpText('specification.volumeValue')}
          />
        );
      case FlushVolumeMode.Explicit:
      case FlushVolumeMode.PipeVolumePlus:
      case FlushVolumeMode.Distance:
        return (
          <UnitSystemNumberField
            unitSystem={unitSystemPreference}
            customUnitFields={customUnitFields}
            unitInfo={specification.volumeMode === FlushVolumeMode.Distance ? unitLabels[BasicDisplayUnits.Distance] : unitLabels[BasicDisplayUnits.Volume]}
            decimalPlaces={1}
            size="small"
            id="flush-volume-value"
            name="specification.volumeValue"
            value={specification.volumeValue}
            aria-labelledby="flush-volume-value-helper-text"
            onChange={(event) => setFieldValue('specification.volumeValue', sanitiseNumber(event.target.value) ?? 0)}
            onBlur={handleBlur}
            error={hasError('specification.volumeValue')}
            helperText={getErrorHelpText('specification.volumeValue')}
          />
        );
      default:
        return null;
    }
  })();

  return (
    <StyledBox sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <form className={classes.form} onSubmit={handleSubmit}>
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormHelperText id="flush-reference-helper-text" component="label"><Trans id="Reference">Reference</Trans></FormHelperText>
            <TextField
              fullWidth
              color="secondary"
              size="small"
              id="flush-reference"
              name="reference"
              aria-labelledby="flush-reference-helper-text"
              value={values.reference}
              onChange={(event) => setFieldValue('reference', event.target.value)}
              onBlur={handleBlur}
              error={hasError('reference')}
              helperText={getErrorHelpText('reference')}
            />
          </Grid>
          <Grid size={12}>
            <FormHelperText id="flush-recipe-helper-text" component="label"><Trans id="Recipe being flushed">Recipe being flushed</Trans></FormHelperText>
            <TextField
              select
              fullWidth
              color="secondary"
              size="small"
              id="flush-recipe"
              name="backfillRecipeId"
              aria-labelledby="flush-recipe-helper-text"
              value={values.backfillRecipeId}
              onChange={(event) => handleRecipeChange(event.target.value)}
              onBlur={handleBlur}
              error={hasError('backfillRecipeId')}
              helperText={getErrorHelpText('backfillRecipeId') || i18n._('Supplies the paste ahead of the water: its density, rheology and flow rate')}
            >
              {linkableRecipes.map((recipe) => (
                <MenuItem key={recipe.entityId} value={recipe.entityId}>{recipe.reference}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={12} className={classes.section}>
            <Typography variant="caption"><Trans id="Flush pump">Flush pump</Trans></Typography>
          </Grid>
          <Grid size={6}>
            <FormHelperText id="flush-pump-max-pressure-helper-text" component="label"><Trans id="Max Pressure">Max Pressure</Trans></FormHelperText>
            <UnitSystemNumberField
              unitSystem={unitSystemPreference}
              customUnitFields={customUnitFields}
              unitInfo={pressureUnitInfo}
              decimalPlaces={pressureDecimalPlaces}
              size="small"
              id="flush-pump-max-pressure"
              name="specification.flushPumpMaxPressure"
              aria-labelledby="flush-pump-max-pressure-helper-text"
              value={toDisplayPressure(specification.flushPumpMaxPressure)}
              onChange={(event) => setFieldValue('specification.flushPumpMaxPressure', fromDisplayPressure(sanitiseNumber(event.target.value) ?? 0))}
              onBlur={handleBlur}
              error={hasError('specification.flushPumpMaxPressure')}
              helperText={getErrorHelpText('specification.flushPumpMaxPressure')}
            />
          </Grid>
          <Grid size={6}>
            <FormHelperText id="flush-pump-flow-rate-helper-text" component="label"><Trans id="Flow Rate">Flow Rate</Trans></FormHelperText>
            <UnitSystemNumberField
              unitSystem={unitSystemPreference}
              customUnitFields={customUnitFields}
              unitInfo={flowRateUnitInfo}
              decimalPlaces={FLOW_RATE_DECIMAL_PLACES}
              size="small"
              id="flush-pump-flow-rate"
              name="specification.flushPumpFlowRate"
              aria-labelledby="flush-pump-flow-rate-helper-text"
              value={specification.flushPumpFlowRate}
              onChange={(event) => setFieldValue('specification.flushPumpFlowRate', sanitiseNumber(event.target.value) ?? 0)}
              onBlur={handleBlur}
              error={hasError('specification.flushPumpFlowRate')}
              helperText={getErrorHelpText('specification.flushPumpFlowRate')}
            />
          </Grid>

          <Grid size={12} className={classes.section}>
            <Typography variant="caption"><Trans id="Paste pump">Paste pump</Trans></Typography>
          </Grid>
          <Grid size={12}>
            <FormHelperText id="paste-pump-flow-rate-helper-text" component="label"><Trans id="Flow Rate">Flow Rate</Trans></FormHelperText>
            <UnitSystemNumberField
              unitSystem={unitSystemPreference}
              customUnitFields={customUnitFields}
              unitInfo={flowRateUnitInfo}
              decimalPlaces={FLOW_RATE_DECIMAL_PLACES}
              size="small"
              id="paste-pump-flow-rate"
              name="specification.pastePumpFlowRate"
              aria-labelledby="paste-pump-flow-rate-helper-text"
              value={specification.pastePumpFlowRate}
              onChange={(event) => setFieldValue('specification.pastePumpFlowRate', sanitiseNumber(event.target.value) ?? 0)}
              onBlur={handleBlur}
              error={hasError('specification.pastePumpFlowRate')}
              helperText={getErrorHelpText('specification.pastePumpFlowRate') || pastePumpHelpText}
            />
          </Grid>

          <Grid size={12} className={classes.section}>
            <Typography variant="caption"><Trans id="Air purge">Air purge</Trans></Typography>
          </Grid>
          <Grid size={6} className={classes.checkboxCell}>
            <FormControlLabel
              control={(
                <Checkbox
                  color="secondary"
                  id="air-purge-enabled"
                  name="specification.airFlushEnabled"
                  checked={specification.airFlushEnabled}
                  onChange={(event) => {
                    setFieldValue('specification.airFlushEnabled', event.target.checked);
                    if (!event.target.checked && specification.volumeMode === FlushVolumeMode.AirStartVolume) {
                      setFieldValue('specification.volumeMode', FlushVolumeMode.PipeVolumeMultiple);
                    }
                  }}
                />
              )}
              label={i18n._('Available')}
            />
          </Grid>
          <Grid size={6}>
            <FormHelperText id="air-purge-max-pressure-helper-text" component="label"><Trans id="Max Pressure">Max Pressure</Trans></FormHelperText>
            <UnitSystemNumberField
              unitSystem={unitSystemPreference}
              customUnitFields={customUnitFields}
              unitInfo={pressureUnitInfo}
              decimalPlaces={pressureDecimalPlaces}
              size="small"
              id="air-purge-max-pressure"
              name="specification.airFlushMaxPressure"
              aria-labelledby="air-purge-max-pressure-helper-text"
              disabled={!specification.airFlushEnabled}
              value={specification.airFlushMaxPressure !== undefined ? toDisplayPressure(specification.airFlushMaxPressure) : undefined}
              onChange={(event) => setFieldValue('specification.airFlushMaxPressure', fromDisplayPressure(sanitiseNumber(event.target.value) ?? 0))}
              onBlur={handleBlur}
              error={hasError('specification.airFlushMaxPressure')}
              helperText={getErrorHelpText('specification.airFlushMaxPressure')}
            />
          </Grid>

          <Grid size={12} className={classes.section}>
            <Typography variant="caption"><Trans id="Flush volume">Flush volume</Trans></Typography>
          </Grid>
          <Grid size={12}>
            <FormHelperText id="flush-volume-mode-helper-text" component="label"><Trans id="Mode">Mode</Trans></FormHelperText>
            <TextField
              select
              fullWidth
              color="secondary"
              size="small"
              id="flush-volume-mode"
              name="specification.volumeMode"
              aria-labelledby="flush-volume-mode-helper-text"
              value={specification.volumeMode}
              onChange={(event) => setFieldValue('specification.volumeMode', Number(event.target.value) as FlushVolumeMode)}
            >
              {FLUSH_VOLUME_MODES.map((mode) => (
                <MenuItem key={mode} value={mode} disabled={mode === FlushVolumeMode.AirStartVolume && !specification.airFlushEnabled}>
                  {flushVolumeModeLabel(mode)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {volumeValueField && (
            <Grid size={12}>
              <FormHelperText id="flush-volume-value-helper-text" component="label">{flushVolumeValueLabel(specification.volumeMode)}</FormHelperText>
              {volumeValueField}
            </Grid>
          )}

          <Grid size={12}>
            <Divider className={classes.fullWidthDivider} />
            {!route && (
              <Alert severity="info"><Trans id="Select a route to plan the flush.">Select a route to plan the flush.</Trans></Alert>
            )}
            {replanning && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {i18n._("This flush's planned route no longer exists in the network. Saving re-plans it on the current route.")}
              </Alert>
            )}
            {route && !linkedRecipe && (
              <Alert severity="info"><Trans id="Select the recipe the water will displace.">Select the recipe the water will displace.</Trans></Alert>
            )}
            {route && linkedRecipe && !result && (
              <Alert severity="info">
                <Trans id="Enter the pump pressures and flow rates to see the flush plan.">Enter the pump pressures and flow rates to see the flush plan.</Trans>
              </Alert>
            )}
            {result && (
              <FlushDetails
                specification={specification}
                result={toFlushResultDto(result)}
                linkedRecipeReference={linkedRecipe?.reference}
                unitSystemPreference={unitSystemPreference}
                customUnitFields={customUnitFields}
                scrub={scrub}
              />
            )}
          </Grid>
        </Grid>

        <FormActions
          isUpdate={!!flushRecipe}
          isSubmitting={isSubmitting}
          isValid={isValid}
          isOptimised={!!result}
          dirty={dirty}
          resetForm={resetForm}
          onCancel={onCancel}
          classes={classes}
        />
      </form>
    </StyledBox>
  );
};

export default FlushForm;
